import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { ParallaxProvider } from 'react-scroll-parallax';
import { useDispatch } from 'react-redux';
import { checkAuth } from './redux/slices/authSlice';
import { Toaster } from 'react-hot-toast';
import { detectUserCountry, detectUserLocationWithPlaceName } from './redux/slices/locationSlice';

// Core components
import Nav from './components/nav/Nav';
import Footer from './components/footer/Footer';
import PrivateRoute from './routes/PrivateRoute';
import PublicRoute from './routes/PublicRoute';
import ScrollToTop from './ScrollToTop';
import StripeVerifiedRoute from './components/stripe/StripeVerifiedRoute';

// Pages
import HomePage from './pages/home/HomePage';
import AboutPage from './pages/about/AboutPage';

// New Auth Pages
import { Login, Register, ForgotPassword, ResetPassword, EmailVerification, CheckEmail, CustomEmailVerification } from './pages/auth';
import SocialCallback from './pages/auth/SocialCallback';
import MessagePage from './pages/messages/MessagePage';
import ContactPage from './pages/contact/ContactPage.js';
import Dashboard from './pages/dashboard/DashboardPage';
import Cart from './pages/cart/CartPage.js';
import BrowsePage from './pages/browse/BrowsePage';
import ProductPage from './pages/product/ProductPage';
import MerchantPage from './pages/merchant/MerchantPage';
import SearchPage from './pages/SearchPage';

// New Checkout Pages
import CheckoutPage from './pages/checkout/CheckoutPage';
import ConfirmationPage from './pages/checkout/ConfirmationPage';

// Merchant
import ListingForm from './components/listings/ListingForm';
import Favorites from './components/favorites/Favorites';
import MerchantOnboarding from './components/stripe/MerchantOnboarding';
import ShippingStatusPage from './pages/merchant/ship/ShippingStatusPage';

// Review and Return Pages
import ProductReviewPage from './pages/product/components/ProductReviewPage';
import ReturnRequestPage from './pages/dashboard/returns/ReturnRequestPage';
import RequestReturnPage from './pages/dashboard/returns/RequestReturnPage';
import ProcessReturnPage from './pages/dashboard/returns/ProcessReturnPage';
import ProductConditionUpdatePage from './pages/dashboard/returns/ProductConditionUpdatePage';
import ReturnShippingPage from './pages/dashboard/returns/ReturnShippingPage';
import ReturnLabelSuccessPage from './pages/dashboard/returns/ReturnLabelSuccessPage';
import CustomerReviewPage from './pages/dashboard/components/CustomerReviewPage';
import Overview from './pages/dashboard/components/Overview';
import ReviewsPage from './pages/review/ReviewsPage';

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(checkAuth());
    dispatch(detectUserCountry());
    dispatch(detectUserLocationWithPlaceName());
  }, [dispatch]);

  return (
    <ParallaxProvider>
    <Router>
      <ScrollToTop />
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: '#FFFFFF',
              color: '#000000',
            },
          }}
        />
        <div className="App flex flex-col min-h-screen">
          <Nav />
          <main className="flex-grow">
            <Switch>
              <Route exact path="/" component={HomePage} />
              <Route path="/browse" component={BrowsePage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/contact" component={ContactPage} />
              <PublicRoute path="/login" component={Login} restricted={true} />
              <PublicRoute path="/register" component={Register} restricted={true} />
              <PublicRoute path="/signup" component={Register} restricted={true} />
              
              {/* Review routes first - more specific paths should be before generic paths */}
              <PrivateRoute 
                path="/fulfillmentgroup/:fulfillmentGroupId/product/:productId/review" 
                component={ProductReviewPage} 
              />
              <PrivateRoute
                path="/fulfillmentgroup/:fulfillmentGroupId/review-products"
                component={ProductReviewPage}
              />
              
              {/* Generic product and merchant routes */}
              <Route path="/product/:id" component={ProductPage} />
              <Route path="/merchant/:id" component={MerchantPage} exact />
              <Route path="/search" component={SearchPage} />
              
              {/* Auth Routes */}
              <PublicRoute path="/forgot-password" component={ForgotPassword} restricted={true} />
              <PublicRoute path="/reset-password" component={ResetPassword} restricted={false} />
              <PublicRoute path="/auth/reset-password" component={ResetPassword} restricted={false} />
              <PublicRoute path="/auth/custom-reset-password" component={ResetPassword} restricted={false} />
              <PublicRoute path="/auth/confirm" component={EmailVerification} restricted={false} />
              <PublicRoute path="/auth/custom-confirm" component={CustomEmailVerification} restricted={false} />
              <PublicRoute path="/auth/check-email" component={CheckEmail} restricted={false} />
              <PublicRoute path="/auth/callback" component={SocialCallback} restricted={false} />
              
              {/* Protected Routes */}
              <PrivateRoute path="/dashboard" component={Dashboard} />
              <PrivateRoute path="/reviews/user/:userId" component={ReviewsPage} />
              <PrivateRoute path="/cart" component={Cart} />
              <PrivateRoute exact path="/checkout" component={CheckoutPage} />
              <PrivateRoute path="/checkout/confirmation" component={ConfirmationPage} />
              {/* Message Routes - Order Matters: More specific first */}
              <PrivateRoute path="/message/order/:orderId" component={MessagePage} />
              <PrivateRoute path="/message/fg/:fulfillmentGroupId" component={MessagePage} />
              <PrivateRoute path="/message/fulfillment-group/:fulfillmentGroupId" component={MessagePage} />
              <PrivateRoute path="/message/product/:productId" component={MessagePage} />
              <PrivateRoute exact path="/message/:chatId?" component={MessagePage} />
              <PrivateRoute path="/favorites" component={Favorites} />
              <PrivateRoute path="/merchant/verify" component={MerchantOnboarding} />
              <PrivateRoute path="/merchant/ship/:fulfillmentGroupId" component={ShippingStatusPage} exact />
              
              {/* Review and Return Routes */}
              <PrivateRoute path="/returns/initiate/:fulfillmentGroupId/:orderItemComponentId" component={ReturnRequestPage} />
              <PrivateRoute path="/dashboard/returns/request/:fulfillmentGroupId" component={RequestReturnPage} />
              <PrivateRoute path="/dashboard/returns/process/:fulfillmentGroupId" component={ProcessReturnPage} />
              <PrivateRoute path="/dashboard/returns/update-condition/:fulfillmentGroupId" component={ProductConditionUpdatePage} />
              <PrivateRoute path="/dashboard/returns/shipping/:orderItemId" component={ReturnShippingPage} />
              <PrivateRoute path="/dashboard/returns/:orderItemId/success" component={ReturnLabelSuccessPage} />
              <PrivateRoute 
                path="/fulfillmentgroup/:fulfillmentGroupId/customer/:customerId/review" 
                component={CustomerReviewPage} 
              />
              <PrivateRoute 
                path="/order/:orderId/customer/:customerId/review" 
                component={CustomerReviewPage} 
              />
              <PrivateRoute path="/dashboard/customer-review/:customerId" component={CustomerReviewPage} />
              
              {/* Listing Routes */}
              <StripeVerifiedRoute exact path="/listing/upload" component={ListingForm} />
              <PrivateRoute path="/listing/edit/:id" component={ListingForm} />
              
              {/* Product Routes */}
              <Route path="/products" render={() => <Redirect to="/browse" />} />
              
              {/* 404 Route */}
              <Route path="*" render={() => <h1 className="text-center py-12 text-xl">Page not found</h1>} />
            </Switch>
          </main>
          <Footer />
        </div>
      </Router>
      </ParallaxProvider>
  );
}

export default App; 